/**
 * The tournament's emblem and hero banner, fetched with the credential and rendered from an
 * object URL.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY A HOOK AND NOT AN `<img src>`
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * The same reason `useOfficialPhoto` is a hook, and this file is deliberately its twin: every
 * call to this API carries the device credential in an `Authorization` header, and a browser
 * attaches no header of its own to an image request. `<img src="/api/brand/emblem">` would
 * arrive anonymously, be refused by the deny-by-default gate, and render as a broken-image glyph
 * in the masthead of every page in the Control Centre. So the bytes are fetched through the same
 * client as everything else and handed to the `<img>` — or to a CSS `background-image` — as a
 * `blob:` address the browser can read without asking anybody.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SESSION CACHE, AND WHY THIS ONE HAS SUBSCRIBERS
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * The cache is `useOfficialPhoto`'s, for its reasons: two slots, at most one request each per
 * session, failures cached as `null` because a masthead's answer to "is there an emblem" is
 * stable for the session, and nothing revoked — an object URL is only safely revoked when no
 * consumer holds it, and with a shared cache no component can know that. Two small images live
 * for the life of the document, which is a far smaller deliberate leak than the roster's fifty.
 *
 * **What is different, and why.** A photograph cannot change while the Control Centre is open;
 * an emblem can, because Tournament Configuration §01 is a screen in this same application with
 * an upload control on it. A cache with no way to be told would leave the TD looking at the old
 * emblem in the masthead directly above the new one in the configuration panel — which reads as
 * the upload having failed. So the cache carries a subscriber set, `invalidateBrandAsset` clears
 * one slot and re-fetches it for whoever is mounted, and the panel calls it after every accepted
 * write. It is not a general store: it holds two entries and answers one question about each.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THREE STATES, AND WHY THERE IS NO FOURTH
 * ═════════════════════════════════════════════════════════════════════════════
 *
 *   · `null` **and not loading** — no artwork is set for this slot, or the fetch failed. The
 *     caller draws exactly what it drew before brand assets existed: the empty emblem box, the
 *     gradient hero band.
 *   · `null` **and loading** — the same drawing. The masthead must not flash a hole.
 *   · an asset — draw it.
 *
 * **A failure is not a fourth state and gets no error surface.** An emblem that cannot be read is
 * not something the Technical Delegate can act on from the Command Desk, and the honest render is
 * the frame the page has always had. What must never happen is a broken image, which reads as a
 * fault in the tournament's own record.
 */

import { useEffect, useState } from "react";

import { fetchBrandAsset } from "../../api/client.js";
import type { BrandAssetKindView } from "../../api/types.js";

/** What a settled slot holds: an object URL and what the file is called, or nothing at all. */
export interface BrandAsset {
  /** A `blob:` URL. */
  url: string;
  /** The uploaded name, from the response's `Content-Disposition`. Null where it was absent. */
  filename: string | null;
  /** The blob's size in bytes, which is what Configuration §01's `97 KB` is computed from. */
  bytes: number;
}

/** Settled answers, keyed on slot. `null` means "there is no readable artwork for this slot". */
const settled = new Map<BrandAssetKindView, BrandAsset | null>();

/** Requests in flight, so two consumers mounting together share one fetch rather than two. */
const inFlight = new Map<BrandAssetKindView, Promise<BrandAsset | null>>();

/** Everyone currently drawing a slot, so an upload reaches the masthead as well as the panel. */
const subscribers = new Map<BrandAssetKindView, Set<(asset: BrandAsset | null) => void>>();

function listeners(kind: BrandAssetKindView): Set<(asset: BrandAsset | null) => void> {
  const existing = subscribers.get(kind);
  if (existing !== undefined) return existing;
  const created = new Set<(asset: BrandAsset | null) => void>();
  subscribers.set(kind, created);
  return created;
}

/**
 * The one fetch per slot. Resolves — it never rejects — because artwork that cannot be read is
 * not a finding, and every caller's fallback is the frame it drew before.
 */
function assetFor(kind: BrandAssetKindView): Promise<BrandAsset | null> {
  const done = settled.get(kind);
  if (done !== undefined) return Promise.resolve(done);

  const pending = inFlight.get(kind);
  if (pending !== undefined) return pending;

  const request = fetchBrandAsset(kind)
    .then(
      (fetched): BrandAsset => ({
        url: URL.createObjectURL(fetched.blob),
        filename: fetched.filename,
        bytes: fetched.blob.size,
      }),
    )
    .catch(() => null)
    .then((asset) => {
      settled.set(kind, asset);
      inFlight.delete(kind);
      for (const notify of listeners(kind)) notify(asset);
      return asset;
    });

  inFlight.set(kind, request);
  return request;
}

/**
 * Forget one slot and fetch it again, telling everyone drawing it.
 *
 * Called by Tournament Configuration §01 after an accepted upload or removal, so the masthead
 * above the panel changes in the same breath as the panel does. Not called anywhere else: a
 * screen that invalidated on mount would be re-fetching on every navigation, which is the exact
 * behaviour the cache exists to prevent.
 */
export function invalidateBrandAsset(kind: BrandAssetKindView): void {
  settled.delete(kind);
  inFlight.delete(kind);
  // Nothing is revoked, for the reason in the header: a consumer may still be drawing the old
  // URL, and two small images are a cheaper price than a masthead that goes blank on a save.
  void assetFor(kind);
}

/**
 * Empties the cache. **For tests only** — one test file's stubbed `fetch` and stubbed
 * `URL.createObjectURL` must not answer the next file's, and module state outlives both.
 */
export function resetBrandAssetCache(): void {
  settled.clear();
  inFlight.clear();
  subscribers.clear();
}

export interface BrandAssetState {
  /** The artwork, or null when there is none to draw. */
  asset: BrandAsset | null;
  /** True while a fetch is in flight. The caller draws its own empty frame either way. */
  loading: boolean;
}

export function useBrandAsset(kind: BrandAssetKindView): BrandAssetState {
  // A cached asset is drawn on the FIRST render, not after one. A masthead that showed an empty
  // box on every re-render before settling back to the same emblem would read as the tournament
  // record flickering between two answers.
  const [asset, setAsset] = useState<BrandAsset | null>(() => settled.get(kind) ?? null);
  const [loading, setLoading] = useState(() => !settled.has(kind));

  useEffect(() => {
    let cancelled = false;

    const notify = (next: BrandAsset | null): void => {
      if (cancelled) return;
      setAsset(next);
      setLoading(false);
    };
    listeners(kind).add(notify);

    const known = settled.get(kind);
    if (known !== undefined) {
      setAsset(known);
      setLoading(false);
    } else {
      setLoading(true);
      // `cancelled` rather than an AbortController: the request is shared with every other
      // consumer of this slot, so tearing this component down must not abort it — it must only
      // stop THIS component setting state after it has gone.
      void assetFor(kind).then(notify);
    }

    return () => {
      cancelled = true;
      listeners(kind).delete(notify);
    };
  }, [kind]);

  return { asset, loading };
}
